import { gql, useMutation, useQuery } from '@apollo/client'
import { useCallback, useRef, useState } from 'react'

import {
  LinkAnonymousAccount,
  LinkAnonymousAccountMutation,
  LinkAnonymousAccountMutationVariables,
  Login,
  SaveOauthToken,
  SignUpUserFragmentDoc,
} from '@/clients/utoniq-core/schema'
import { config } from '@/config'
import {
  useAuth,
  useCurrentUser,
  useMessage,
  useRouter,
  useTranslation,
} from '@/core'
import { useAnalytics } from '@/core/analytics'
import { makeViewModel } from '@/core/architecture'
import { browserDetect } from '@/core/native/device'
import {
  getRedirectAfterLoginPath,
  getUserInfoRegisterRedirect,
  setCredential,
  setRedirectAfterLoginPath,
} from '@/core/native/storage'

import { fragments } from './template/fragments'
import { MailAuthFormProps } from './template/MailAuthForm'
import { SignUpFormProps } from './template/SignUpForm'
type State = {
  verifyEmailSending: boolean
  loading: boolean
}

export type Action = {
  signUp: SignUpFormProps['onSubmitSignUp']
  signUpWithGoogle: () => Promise<void>
  signUpWithApple: () => Promise<void>
  signUpWithTwitter: () => Promise<void>
  verifyAccount: MailAuthFormProps['onSubmitMailAuth']
  resendVerifyMail: MailAuthFormProps['onResendAuthMail']
}

const VERIFY_CODE = gql`
  mutation VerifyCode($email: String!, $code: String!) {
    verifyCode(email: $email, code: $code) {
      id
      verified
    }
  }
`

const SEND_VERIFICATION_EMAIL = gql`
  mutation SendVerificationEmail($email: String!) {
    sendVerificationEmail(email: $email) {
      id
      email
    }
  }
`

const SIGNUP_AS_User = gql`
  mutation SignupAsUser($inputs: SignupAsUserInput!) {
    signupAsUser(inputs: $inputs) {
      ...SignUpUser
    }
  }
  ${SignUpUserFragmentDoc}
`

const initialValues = {
  displayName: '',
  email: '',
  password: '',
}

export const useViewModel = makeViewModel<State, Action>(() => {
  const { t: tMailAuth } = useTranslation('mailAuth')
  const { router, routerPages } = useRouter<{ redirectUri?: string }>()
  const { loginWithEmail, signInWithTwitter, signInWithApple } = useAuth()
  const { user: currentUser } = useCurrentUser()
  const [account, setAccount] = useState(initialValues)
  const { showError } = useMessage()
  const [loading, setLoading] = useState(false)
  const { refetch: refetchUser } = useQuery(Login, { skip: true })
  const [verifyCode] = useMutation(VERIFY_CODE)
  const [sendVerifyEmail, { loading: verifyEmailSending }] = useMutation(
    SEND_VERIFICATION_EMAIL
  )

  const [signupAsUser] = useMutation(SIGNUP_AS_User)
  const [saveOauthToken] = useMutation(SaveOauthToken)
  const { analytics } = useAnalytics()
  const redirectUriRef = useRef<string | undefined>(router.query?.redirectUri)

  const handleSignUpSuccess = useCallback(async () => {
    const { data } = await refetchUser()
    const userInfoRegisterRedirect = await getUserInfoRegisterRedirect()
    if (!userInfoRegisterRedirect?.direction) {
      return
    }
    redirectUriRef.current = userInfoRegisterRedirect.direction
    if (
      userInfoRegisterRedirect?.phoneNumber &&
      !(data?.user?.phoneNumber && data?.user?.isPhoneNumberVerified)
    ) {
      redirectUriRef.current = routerPages.StoreUserInfo
    } else if (
      userInfoRegisterRedirect?.address &&
      !data?.user?.address?.fullAddress
    ) {
      redirectUriRef.current = routerPages.StoreDeliveryInfo
    }
    await setRedirectAfterLoginPath(redirectUriRef.current)
  }, [refetchUser, routerPages.StoreDeliveryInfo, routerPages.StoreUserInfo])

  return {
    state: {
      verifyEmailSending,
      loading,
    },
    actions: {
      signUp: async inputs => {
        await analytics?.track('click_register', {
          loginMethod: 'email',
          applicationKey: config.applicationKey,
          isAnonymous: currentUser?.isAnonymous,
        })
        await sendVerifyEmail({ variables: { email: inputs.email } })
          .then(() => {
            setAccount(inputs)
            return
          })
          .catch(async e => {
            await analytics?.track('error_register', {
              loginMethod: 'email',
              error: e.message,
              applicationKey: config.applicationKey,
              isAnonymous: currentUser?.isAnonymous,
            })
            throw e
          })
      },
      signUpWithGoogle: async () => {},
      signUpWithApple: async () => {
        setLoading(true)
        try {
          await analytics?.track('click_register', {
            loginMethod: 'apple.com',
            applicationKey: config.applicationKey,
            isAnonymous: currentUser?.isAnonymous,
          })
          await signInWithApple(handleSignUpSuccess)
          await analytics?.track('register', {
            loginMethod: 'apple.com',
            applicationKey: config.applicationKey,
            isAnonymous: currentUser?.isAnonymous,
          })

          if (config.modules.tutorial) {
            return router.replace(routerPages.Tutorial)
          }

          getRedirectAfterLoginPath().then(({ value }) =>
            router.replace(value ?? routerPages.GlobalHome)
          )
          setLoading(false)
        } catch (e) {
          await analytics?.track('error_register', {
            loginMethod: 'apple.com',
            applicationKey: config.applicationKey,
            isAnonymous: currentUser?.isAnonymous,
          })
          setLoading(false)
          showError(e as Error, { browserName: browserDetect() })
        }
      },

      signUpWithTwitter: async () => {
        await analytics?.track('click_register', {
          loginMethod: 'twitter.com',
          applicationKey: config.applicationKey,
          isAnonymous: currentUser?.isAnonymous,
        })
        setLoading(true)
        signInWithTwitter(handleSignUpSuccess)
          .then(res => {
            if (!res) return

            const { providerId, user, credential } = res

            if (user?.email) {
              saveOauthToken({
                variables: {
                  inputs: {
                    providerId,
                    oauthToken: credential.oauthToken,
                    oauthTokenSecret: credential.oauthTokenSecret,
                  },
                },
              })
            } else {
              // SNS認証結果、アカウントのメアドがない場合は、その他の認証情報をlocalStorageに一時保存する
              setCredential({
                providerId,
                oauthToken: credential.oauthToken || '',
                oauthTokenSecret: credential.oauthTokenSecret || '',
              })
            }
          })
          .then(() =>
            analytics?.track('register', {
              loginMethod: 'twitter.com',
              applicationKey: config.applicationKey,
              isAnonymous: currentUser?.isAnonymous,
            })
          )
          .then(async () => {
            if (config.modules.tutorial) {
              return router.replace(routerPages.Tutorial)
            }

            getRedirectAfterLoginPath().then(({ value }) =>
              router.replace(value ?? routerPages.GlobalHome)
            )
          })
          .catch(async e => {
            await analytics?.track('error_register', {
              loginMethod: 'twitter.com',
              applicationKey: config.applicationKey,
              isAnonymous: currentUser?.isAnonymous,
            })
            showError(e, { browserName: browserDetect() })
          })
          .finally(async () => {
            setLoading(false)
          })
      },
      verifyAccount: async ({ code }) => {
        const {
          data: {
            verifyCode: { verified },
          },
        } = await verifyCode({ variables: { email: account.email, code } })
        if (!verified) throw new Error(tMailAuth('verifyFailed'))

        await signupAsUser({ variables: { inputs: account } })
        await analytics?.track('register', {
          loginMethod: 'email',
          applicationKey: config.applicationKey,
          isAnonymous: currentUser?.isAnonymous,
        })
        const redirectAfterLoginPath = await getRedirectAfterLoginPath()
        await loginWithEmail(
          account.email,
          account.password,
          handleSignUpSuccess
        )

        if (config.modules.tutorial) {
          return router.replace(routerPages.Tutorial)
        }

        if (redirectUriRef.current) {
          await setRedirectAfterLoginPath(redirectUriRef.current)
        }

        if (redirectAfterLoginPath.value) {
          router.replace(redirectAfterLoginPath.value)
          return false
        }

        return true
      },

      resendVerifyMail: async () => {
        await sendVerifyEmail({ variables: { email: account.email } })

        return
      },
    },
  }
})

/**
 * @description
 * sns認証のリダイレクト時に、以下行うhook
 * - userDocを生成する
 * - sns認証のcredentialをuserDocに保持する
 */
export const useSavingSocialUserInfo = () => {
  const { getRedirectResult } = useAuth()
  const [loading, setLoading] = useState(false)
  const [saveOauthToken] = useMutation(SaveOauthToken)
  const [updateProfile] = useMutation(UPDATE_PROFILE)
  const [linkAnonymousAccount] = useMutation<
    LinkAnonymousAccountMutation,
    LinkAnonymousAccountMutationVariables
  >(LinkAnonymousAccount)

  const saveSocialUserInfo = () => {
    setLoading(true)

    return getRedirectResult()
      .then(res => {
        if (!res) return

        const { credential, additionalUserInfo: info, providerId } = res

        return updateProfile({
          variables: {
            inputs: {
              accountId: info.accountId,
              displayName: info.displayName,
              introduction: info.introduction,
              avatarPath: info.profileUrl,
            },
          },
        }).then(() => {
          if (!credential) return

          saveOauthToken({
            variables: {
              inputs: {
                providerId,
                oauthToken: credential.oauthToken,
                oauthTokenSecret: credential.oauthTokenSecret,
              },
            },
          })
        })
      })
      .catch(error => {
        throw error
      })
      .finally(() => setLoading(false))
  }

  return { saveSocialUserInfo, loading, linkAnonymousAccount }
}

const UPDATE_PROFILE = gql`
  mutation updateProfile($inputs: UpdateProfileInput!) {
    updateUserProfile(inputs: $inputs) {
      ...UserProfile
    }
  }
  ${fragments.updateProfile}
`

const LINK_ANONYMOUS_ACCOUNT = gql`
  mutation linkAnonymousAccount($inputs: LinkAnonymousAccountInput!) {
    linkAnonymousAccount(inputs: $inputs) {
      id
    }
  }
`
